/**
 * TemplateEditor — one disclosure template (route /disclosures/templates/:code).
 *
 * 2026-10-07 rewrite (Bill's 2026-10-06 captures of POA_CERT):
 *   - "Who gets it" in plain language, built from the template's columns, the
 *     customer-type rules and the ACTIVE version's "only when" condition — the same
 *     inputs the package builder reads (DisclosureRenderOrchestrator.ResolveAsync).
 *   - "Who signs" from the code's own list of signature rules.
 *   - A version timeline: status, who did what and when, the live badge, and the
 *     actions each status allows (submit / approve / activate / retire / delete
 *     draft), each behind a confirm dialog. History per version from the audit log.
 *   - Preview renders the version you click, never "whichever is active".
 *   - "Preview in package generator" opens /package-generator?template=<code>.
 *
 * Endpoints:
 *   POST   /v3/admin/disclosures/templates                   (settings save)
 *   POST   /v3/admin/disclosures/versions                    (new draft)
 *   PATCH  /v3/admin/disclosures/versions/{id}               (draft settings)
 *   DELETE /v3/admin/disclosures/versions/{id}               (draft only)
 *   POST   /v3/admin/disclosures/versions/{id}/(submit|approve|activate|retire)
 *   GET    /v3/admin/disclosures/versions/{id}/audit
 *   POST   /v3/admin/disclosures/preview                     ({ templateCode, versionId })
 *
 * Approval: the server records who did each step from the sign-in. It does not
 * stop the same person from submitting, approving and activating (no maker/checker
 * rule exists in the code today); the history shows who did each step.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  Archive,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Edit3,
  ExternalLink,
  Eye,
  FileText,
  History,
  PlayCircle,
  Plus,
  RefreshCw,
  Send,
  Settings,
  Trash2,
} from 'lucide-react';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import type { DisclosureVersionRow } from '@/services/disclosuresApi';
import {
  activateVersion,
  approveVersion,
  deleteVersion,
  retireVersion,
  submitVersion,
  versionAuditLog,
  type DisclosureVersionAuditRow,
} from '@/services/disclosuresAdminApi';

import { ConfirmDialog, Modal, PillStatus, TextArea, Toast, useActor, useToast } from './_shared';
import { TemplateEditorModal } from './TemplateEditorModal';
import { VersionEditorModal } from './VersionEditorModal';
import { VersionPreview } from './VersionPreview';
import {
  describeApplicability,
  formatWhen,
  isLive,
  itemFor,
  liveVersion,
  splitCondition,
  statusLabel,
  versionsFor,
} from './disclosureModel';

type TransitionKind = 'submit' | 'approve' | 'activate' | 'retire';

interface TransitionPrompt {
  version: DisclosureVersionRow;
  kind: TransitionKind;
}

const TRANSITION_TEXT: Record<TransitionKind, { title: string; button: string; body: string }> = {
  submit: {
    title: 'Submit for approval',
    button: 'Submit',
    body: 'The draft is locked for editing and goes to the approval queue. It is still not used in live packages.',
  },
  approve: {
    title: 'Approve',
    button: 'Approve',
    body: 'Records your approval. It is still not used in live packages until someone activates it.',
  },
  activate: {
    title: 'Activate',
    button: 'Activate',
    body: 'From the next package built, this version is the one used.',
  },
  retire: {
    title: 'Retire',
    button: 'Retire',
    body: 'The version stops being usable. A reason is required and is kept in the history.',
  },
};

export function TemplateEditor(): JSX.Element {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { envelope, refresh, options, state } = useDisclosuresConfig();
  const actor = useActor();
  const { toast, push, clear } = useToast();

  const [editMetaOpen, setEditMetaOpen] = useState<boolean>(false);
  const [versionModal, setVersionModal] = useState<{ open: boolean; initial: DisclosureVersionRow | null }>({
    open: false,
    initial: null,
  });
  const [confirmDelete, setConfirmDelete] = useState<DisclosureVersionRow | null>(null);
  const [transition, setTransition] = useState<TransitionPrompt | null>(null);
  const [reason, setReason] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [historyId, setHistoryId] = useState<number | null>(null);
  const [history, setHistory] = useState<Record<number, ReadonlyArray<DisclosureVersionAuditRow> | 'loading' | string>>({});

  const template = envelope.templates.find((t) => String(t.code ?? '').toUpperCase() === code.toUpperCase());
  const versions = useMemo(() => versionsFor(envelope.versions, code), [envelope.versions, code]);
  const live = useMemo(() => liveVersion(versions), [versions]);
  const applicability = useMemo(
    () => (template ? describeApplicability(template, live, options) : []),
    [template, live, options],
  );
  const sig = itemFor(options?.signatureRequirements, template?.signatureRequirement);
  const variants = envelope.variants.filter(
    (v) => String(v.variantKey ?? '') !== '' && String(v.variantKey) === String(template?.variantKey ?? '') && v.isActive !== false,
  );

  const loadHistory = useCallback(async (versionId: number): Promise<void> => {
    setHistory((h) => ({ ...h, [versionId]: 'loading' }));
    const r = await versionAuditLog(versionId);
    setHistory((h) => ({ ...h, [versionId]: r.success ? r.data?.rows ?? [] : r.error ?? 'Could not load the history.' }));
  }, []);

  const toggleHistory = (versionId: number): void => {
    if (historyId === versionId) {
      setHistoryId(null);
      return;
    }
    setHistoryId(versionId);
    void loadHistory(versionId);
  };

  const onDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      const result = await deleteVersion(Number(confirmDelete.versionId));
      if (!result.success) {
        push('err', result.error ?? 'Delete failed.');
        return;
      }
      push('ok', `Draft ${String(confirmDelete.versionNumber)} deleted.`);
      setConfirmDelete(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  }, [confirmDelete, push, refresh]);

  const runTransition = useCallback(async (): Promise<void> => {
    if (!transition) return;
    const id = Number(transition.version.versionId);
    const why = reason.trim();
    if (transition.kind === 'retire' && !why) {
      push('err', 'A reason is required to retire a version.');
      return;
    }
    setBusy(true);
    try {
      const fn = {
        submit: () => submitVersion(id, actor, why || undefined),
        approve: () => approveVersion(id, actor, why || undefined),
        activate: () => activateVersion(id, actor, why || undefined),
        retire: () => retireVersion(id, actor, why),
      }[transition.kind];
      const result = await fn();
      if (!result.success) {
        push('err', result.error ?? `${TRANSITION_TEXT[transition.kind].title} failed.`);
        return;
      }
      push(
        'ok',
        transition.kind === 'activate'
          ? `Version ${String(transition.version.versionNumber)} is now active — the next package built uses it.`
          : `${TRANSITION_TEXT[transition.kind].title}: done for version ${String(transition.version.versionNumber)}.`,
      );
      setTransition(null);
      setReason('');
      void refresh();
      if (historyId === id) void loadHistory(id);
    } finally {
      setBusy(false);
    }
  }, [transition, reason, actor, push, refresh, historyId, loadHistory]);

  if (!code) {
    return <div className="empty">No template code in the address. <Link to="/disclosures/templates">Back to the list</Link>.</div>;
  }

  const versionLink = (v: DisclosureVersionRow): string =>
    `/disclosures/templates/${encodeURIComponent(code)}/versions/${encodeURIComponent(String(v.versionNumber))}`;

  return (
    <>
      <Toast toast={toast} onClose={clear} />

      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/" className="back-link">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/disclosures" className="back-link">Disclosure Engine</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/disclosures/templates" className="back-link">Templates</Link>
        <span aria-hidden="true"> / </span>
        <span><code>{code}</code></span>
      </nav>

      <Link to="/disclosures/templates" className="back-link">
        <ArrowLeft size={14} /> Back to templates
      </Link>

      <section className="page-header" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 className="page-header__title">{template?.displayName ? String(template.displayName) : code}</h1>
          <p className="page-header__subtitle page-intro" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <code>{code}</code>
            {template && (template.isActive === false ? <span className="pill pill--muted">Switched off</span> : <span className="pill pill--ok">Switched on</span>)}
            {live ? (
              <span className="pill pill--ok">Live: version {String(live.versionNumber)}</span>
            ) : (
              <span className="pill pill--err">No active version</span>
            )}
          </p>
          {template?.description && <p style={{ fontSize: 13, margin: '4px 0 0' }}>{String(template.description)}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void refresh()} disabled={state === 'loading'}>
            <RefreshCw size={12} /> Refresh
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditMetaOpen(true)} disabled={!template || !options}>
            <Settings size={12} /> Edit who gets it / who signs
          </button>
          <Link className="btn btn--ghost btn--sm" to={`/package-generator?template=${encodeURIComponent(code)}`}>
            <ExternalLink size={12} /> Preview in package generator
          </Link>
        </div>
      </section>

      {!template && state !== 'loading' && (
        <div className="banner banner--err" role="alert">Template {code} is not in the loaded catalog. Refresh to retry.</div>
      )}

      {template && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          <section className="card">
            <div className="card-header"><h2 style={{ margin: 0 }}>Who gets it</h2></div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
              {applicability.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
            {variants.length > 0 && (
              <p style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 0 }}>
                Variant family <code>{String(template.variantKey)}</code>: the text can be swapped for{' '}
                {variants.map((v) => String(v.variantCode)).join(', ')} by plan or customer type (Variants screen).
              </p>
            )}
            <p style={{ fontSize: 11, color: 'var(--ink-500)', marginBottom: 0 }}>
              Changes apply to the next package built (no redeploy, no cache). A package already built for an application keeps the text it was built with.
            </p>
          </section>

          <section className="card">
            <div className="card-header"><h2 style={{ margin: 0 }}>Who signs</h2></div>
            <p style={{ fontSize: 15, fontWeight: 600, margin: '8px 0 4px' }}>
              {sig?.label ?? (template.signatureRequirement ? `${String(template.signatureRequirement)} — not a rule the package builder knows (treated as every signer)` : '—')}
            </p>
            {sig && <p style={{ fontSize: 13, margin: 0 }}>{sig.meaning}</p>}
            <dl className="kv-grid" style={{ marginTop: 10 }}>
              <dt>Signatures expected</dt>
              <dd>{String(template.defaultSignatureCount ?? '—')}</dd>
              <dt>Pages expected</dt>
              <dd>{String(template.defaultPageCount ?? '—')}</dd>
              <dt>Copies</dt>
              <dd>{itemFor(options?.multiplicities, template.multiplicity)?.label ?? String(template.multiplicity ?? '—')}</dd>
              <dt>Last changed</dt>
              <dd>{formatWhen(template.updatedAt)} {template.updatedBy ? `by ${String(template.updatedBy)}` : ''}</dd>
            </dl>
          </section>
        </div>
      )}

      <section className="card card--inset">
        <div className="card-header">
          <h2 style={{ margin: 0 }}>Versions ({versions.length})</h2>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setVersionModal({ open: true, initial: null })}
            disabled={!template || !options}
          >
            <Plus size={12} /> New draft
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-500)', margin: '4px 0 8px' }}>
          Draft → Pending approval → Approved → Active. Only the active version is used in live packages;
          activating a version retires the one it replaces.
        </p>

        {versions.length === 0 && (
          <div className="empty">
            No versions yet, so this template is never used in a live package. Create the first draft above.
          </div>
        )}

        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {versions.map((v) => {
            const id = Number(v.versionId);
            const status = String(v.status ?? '');
            const conds = splitCondition(v.conditionExpression);
            const h = history[id];
            return (
              <li
                key={id}
                style={{
                  borderLeft: `4px solid ${isLive(v) ? 'var(--pcb-green)' : status === 'Retired' ? 'var(--ink-300)' : 'var(--pcb-blue)'}`,
                  padding: '10px 12px',
                  margin: '0 0 10px',
                  background: 'var(--surface-card)',
                  borderRadius: 6,
                  boxShadow: '0 1px 2px rgba(15,22,40,0.08)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong>Version {String(v.versionNumber)}</strong>
                  <PillStatus status={status} />
                  {isLive(v) && <span className="pill pill--ok">Used in live packages</span>}
                  <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                    {v.pageCount ?? 0} page(s) · {v.signatureCount ?? 0} signature(s)
                  </span>
                  <div style={{ flex: 1 }} />
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPreviewId(previewId === id ? null : id)}>
                    <Eye size={12} /> {previewId === id ? 'Hide preview' : 'Preview'}
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate(versionLink(v))}>
                    <FileText size={12} /> {status === 'Draft' ? 'Edit text' : 'View text'}
                  </button>
                  {status === 'Draft' && (
                    <>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setVersionModal({ open: true, initial: v })}>
                        <Edit3 size={12} /> Settings
                      </button>
                      <button type="button" className="btn btn--primary btn--sm" onClick={() => setTransition({ version: v, kind: 'submit' })}>
                        <Send size={12} /> Submit for approval
                      </button>
                      <button type="button" className="btn btn--ghost btn--sm" style={{ color: 'var(--pcb-red)' }} onClick={() => setConfirmDelete(v)}>
                        <Trash2 size={12} /> Delete draft
                      </button>
                    </>
                  )}
                  {status === 'InReview' && (
                    <button type="button" className="btn btn--primary btn--sm" onClick={() => setTransition({ version: v, kind: 'approve' })}>
                      <CheckCircle2 size={12} /> Approve
                    </button>
                  )}
                  {status === 'Approved' && (
                    <button type="button" className="btn btn--primary btn--sm" onClick={() => setTransition({ version: v, kind: 'activate' })}>
                      <PlayCircle size={12} /> Activate
                    </button>
                  )}
                  {status !== 'Retired' && status !== 'Draft' && (
                    <button type="button" className="btn btn--ghost btn--sm" style={{ color: 'var(--pcb-red)' }} onClick={() => setTransition({ version: v, kind: 'retire' })}>
                      <Archive size={12} /> Retire
                    </button>
                  )}
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => toggleHistory(id)}>
                    <History size={12} /> History {historyId === id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                </div>

                <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <span>Written by {String(v.authoredBy ?? '—')} · {formatWhen(v.authoredAt)}</span>
                  {v.submittedBy && <span>Submitted by {String(v.submittedBy)} · {formatWhen(v.submittedAt)}</span>}
                  {v.approvedBy && <span>Approved by {String(v.approvedBy)} · {formatWhen(v.approvedAt)}</span>}
                  {v.activatedBy && <span>Activated by {String(v.activatedBy)} · {formatWhen(v.activatedAt)}</span>}
                  {v.retiredBy && <span>Retired by {String(v.retiredBy)} · {formatWhen(v.retiredAt)}{v.retirementReason ? ` — ${String(v.retirementReason)}` : ''}</span>}
                  {v.rejectedBy && <span>Sent back by {String(v.rejectedBy)} · {formatWhen(v.rejectedAt)}{v.rejectionReason ? ` — ${String(v.rejectionReason)}` : ''}</span>}
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Only when:{' '}
                  {conds.length === 0
                    ? 'always (when the template applies)'
                    : conds.map((c) => itemFor(options?.presenceConditions, c)?.label ?? `unknown "${c}"`).join(' OR ')}
                </div>
                {v.notes && <div style={{ fontSize: 12, marginTop: 4, color: 'var(--ink-600)' }}>Notes: {String(v.notes)}</div>}

                {previewId === id && (
                  <div style={{ marginTop: 10 }}>
                    <VersionPreview templateCode={code} version={v} autoRun />
                  </div>
                )}

                {historyId === id && (
                  <div style={{ marginTop: 10 }}>
                    {h === 'loading' || h === undefined ? (
                      <span style={{ fontSize: 12 }}>Loading history…</span>
                    ) : typeof h === 'string' ? (
                      <div className="banner banner--err">{h}</div>
                    ) : h.length === 0 ? (
                      <span style={{ fontSize: 12 }}>No history recorded.</span>
                    ) : (
                      <table className="table table--striped" style={{ fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Who</th>
                            <th>Change</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {h.map((r, i) => (
                            <tr key={`${String(r.auditId ?? i)}`}>
                              <td>{formatWhen(r.timestamp)}</td>
                              <td>{r.actor ?? '—'}</td>
                              <td>
                                {r.fromStatus ? `${statusLabel(r.fromStatus)} → ` : 'Created as '}
                                {statusLabel(r.toStatus)}
                              </td>
                              <td>{r.reason ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      <TemplateEditorModal
        open={editMetaOpen}
        mode="edit"
        initial={template ?? null}
        onClose={() => setEditMetaOpen(false)}
        onSaved={(c) => {
          setEditMetaOpen(false);
          push('ok', `${c} saved. The next package built uses these settings.`);
          void refresh();
        }}
        onError={(msg) => push('err', msg)}
      />

      <VersionEditorModal
        open={versionModal.open}
        templateCode={code}
        initial={versionModal.initial}
        onClose={() => setVersionModal({ open: false, initial: null })}
        onSaved={() => {
          setVersionModal({ open: false, initial: null });
          push('ok', 'Draft saved. It is not used in live packages until it is approved and activated.');
          void refresh();
        }}
        onError={(msg) => push('err', msg)}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete this draft?"
        message={
          <span>
            Draft version <strong>{String(confirmDelete?.versionNumber ?? '')}</strong> and its history are removed.
            Only drafts can be deleted; other versions are retired instead.
          </span>
        }
        busy={busy}
        onConfirm={() => void onDeleteConfirm()}
        onCancel={() => setConfirmDelete(null)}
      />

      <Modal
        open={transition !== null}
        onClose={() => {
          if (!busy) {
            setTransition(null);
            setReason('');
          }
        }}
        title={transition ? `${TRANSITION_TEXT[transition.kind].title}: version ${String(transition.version.versionNumber)}` : ''}
        width={500}
        closeOnBackdrop={!busy}
        footer={
          <>
            <button type="button" className="btn btn--ghost" onClick={() => { setTransition(null); setReason(''); }} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={transition?.kind === 'retire' ? 'btn btn--danger' : 'btn btn--primary'}
              onClick={() => void runTransition()}
              disabled={busy || (transition?.kind === 'retire' && reason.trim().length === 0)}
            >
              {busy ? 'Working…' : transition ? TRANSITION_TEXT[transition.kind].button : ''}
            </button>
          </>
        }
      >
        {transition && (
          <>
            <p style={{ fontSize: 13 }}>{TRANSITION_TEXT[transition.kind].body}</p>
            {transition.kind === 'activate' && (
              <div className="banner banner--warn" style={{ fontSize: 13 }}>
                {live
                  ? `Version ${String(live.versionNumber)} is active now and will be retired.`
                  : 'No version is active now, so this template starts appearing in packages it applies to.'}
              </div>
            )}
            {transition.kind === 'retire' && isLive(transition.version) && (
              <div className="banner banner--err" style={{ fontSize: 13 }}>
                This is the active version. Retiring it leaves the template with no active version: packages that
                need it will report it as missing until another version is activated.
              </div>
            )}
            <label className="form-field" style={{ display: 'block', marginTop: 8 }}>
              <span className="form-field__label">Reason{transition.kind === 'retire' ? ' (required)' : ' (optional)'}</span>
              <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <p style={{ fontSize: 12, color: 'var(--ink-500)' }}>Recorded in the history as {actor}.</p>
          </>
        )}
      </Modal>
    </>
  );
}
